import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { getFolders } from '../api';

const styles = {
  breadCrumb: {
    my: 1,
    fontSize: '16px',
    color: '#1D2433CC',
    fontWeight: '700',
    cursor: 'pointer',
    maxWidth: '170px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textDecoration: 'underline',
    '&:hover': { opacity: 0.7 },
  },
  inactiveCrumb: {
    textDecoration: 'none',
    cursor: 'default',
    '&:hover': { opacity: 1 },
  },
  separator: {
    mx: 1,
    display: 'flex',
    alignItems: 'center',
    fontWeight: '700',
    color: '#1D2433CC',
    fontSize: '16px',
  },
  returnButton: { fontSize: '15px', my: 2, py: 1, px: 3, fontWeight: 'bold' },
  returnIcon: { fontSize: '15px', fontWeight: 'bold' },
};

const BreadCrumb = props => {
  const {
    breadCrumb,
    setBreadCrumb,
    setData,
    setError,
    setLoading,
    setInitialData,
    setSelectedFilters,
  } = props;

  const breadCrumbLength = breadCrumb.length;

  const handleBreadcrumbClick = useCallback(
    (id, index) => {
      setLoading(true);
      setError('');

      getFolders(id)
        .then(response => {
          setData(response.data);
          setBreadCrumb(prev => prev.slice(0, index + 1));
          setSelectedFilters([]);
        })
        .catch(err => setError(err.message))
        .finally(() => setLoading(false));
    },
    [setBreadCrumb, setData, setError, setLoading, setSelectedFilters]
  );

  const handleBackNavigation = useCallback(() => {
    const isRootLevel = breadCrumbLength === 1;

    if (isRootLevel) {
      setLoading(true);
      setError('');

      getFolders()
        .then(response => {
          setData(response.data);
          setInitialData(response.data);
          setBreadCrumb([]);
          setSelectedFilters([]);
        })
        .catch(err => setError(err.message))
        .finally(() => setLoading(false));
    } else {
      const preLastIndex = breadCrumbLength - 2;
      handleBreadcrumbClick(breadCrumb[preLastIndex]?.id, preLastIndex);
    }
  }, [
    breadCrumb,
    breadCrumbLength,
    handleBreadcrumbClick,
    setBreadCrumb,
    setData,
    setError,
    setInitialData,
    setLoading,
    setSelectedFilters,
  ]);

  return (
    <Box>
      <Stack direction="row" flexWrap="wrap">
        {breadCrumb.map((folder, index) => {
          const isLast = breadCrumbLength === index + 1;

          return (
            <Stack key={folder.id} direction="row" alignItems="center">
              {index > 0 && <Box sx={styles.separator}>{'>'}</Box>}
              <Tooltip
                title={<Typography fontSize={11}>{folder.title}</Typography>}
                arrow
                placement="top"
                slotProps={{
                  popper: {
                    sx: {
                      zIndex: 20000,
                    },
                  },
                }}
              >
                <Box
                  sx={{ ...styles.breadCrumb, ...(isLast ? styles.inactiveCrumb : {}) }}
                  onClick={!isLast ? () => handleBreadcrumbClick(folder.id, index) : undefined}
                >
                  {folder.title}
                </Box>
              </Tooltip>
            </Stack>
          );
        })}
      </Stack>

      {breadCrumbLength > 0 && (
        <Button
          startIcon={<ArrowBackIosIcon style={styles.returnIcon} />}
          variant="contained"
          color="secondary"
          sx={styles.returnButton}
          onClick={handleBackNavigation}
        >
          Return
        </Button>
      )}
    </Box>
  );
};

export default BreadCrumb;
